import { Activity, FileUp, Loader2, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImpactAnalysisSection } from "@/components/steps/impact-analysis-section";
import { PaywallDialog } from "@/components/PaywallDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useUpdateEntityData } from "@/hooks/api/mutate/useUpdateEntityImpactAnalysis";
import { useGeneratePdf } from "@/hooks/api/mutate/useGeneratePdf";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { generatePDFReport } from "@/utils/generate-report";
import { getEfectPerturbatorStatus } from "@/utils/guideProgressCalculator";
import { prepareImpactAnalysisUpdates } from "@/utils/impact-analysis";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { impactAnalysisSnapshotStore } from "@/utils/impactAnalysisSnapshotStore";
import type {
  ImpactAnalysisData,
  LegalRepresentativeData,
} from "@/types/impact-analysis";
import type { ContactData } from "@/types/contact";

type EpPaywallActionType =
  | "analiza_efect_perturbator"
  | "doc_analiza_efect_perturbator_pdf";

export default function AnalyzaEfectPerturbator() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();

  const { data, isLoading, error } = useGetEntityData(currentSessionId);

  const entityData = data?.entity_data;
  const impactAnalysis = entityData?.impact_analysis as
    | ImpactAnalysisData
    | undefined;
  const reprezentantData = entityData?.contact_data?.reprezentantLegal;

  const updateMutation = useUpdateEntityData();
  const generatePdfMutation = useGeneratePdf();
  const { mutateAsync: deductCredits } = useDeductCredits();

  const {
    data: classifCreditCheck,
    hasInsufficientCredits: hasInsufficientClassifCredits,
  } = useCheckActionCredits(
    {
      session_id: currentSessionId || "",
      action_type: CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR,
    },
    !!currentSessionId
  );

  const { isLoading: isCheckingCredits, hasInsufficientCredits } =
    useCheckActionCredits(
      {
        session_id: currentSessionId || "",
        action_type: CREDIT_ACTION_TYPES.DOC_ANALIZA_EFECT_PERTURBATOR_PDF,
      },
      !!currentSessionId
    );

  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallActionType, setPaywallActionType] =
    useState<EpPaywallActionType>("doc_analiza_efect_perturbator_pdf");

  const [snapshotImpactAnalysis, setSnapshotImpactAnalysis] =
    useState<ImpactAnalysisData | null>(null);
  const [snapshotReprezentant, setSnapshotReprezentant] =
    useState<LegalRepresentativeData | null>(null);
  const [snapshotArt9Flag, setSnapshotArt9Flag] = useState(false);
  const rehydratedRef = useRef(false);

  useEffect(() => {
    if (rehydratedRef.current) return;
    if (!currentSessionId) return;
    rehydratedRef.current = true;
    const snapshot = impactAnalysisSnapshotStore.get(currentSessionId);
    if (!snapshot) return;
    setSnapshotImpactAnalysis(snapshot.impactAnalysis);
    setSnapshotReprezentant(snapshot.reprezentantData);
    setSnapshotArt9Flag(snapshot.art9Flag);
    impactAnalysisSnapshotStore.clear(currentSessionId);
  }, [currentSessionId]);

  const isLoadingCredits = isCheckingCredits || generatePdfMutation.isPending;

  const openPaywall = (
    actionType: EpPaywallActionType,
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData,
    art9Flag: boolean
  ) => {
    if (currentSessionId) {
      impactAnalysisSnapshotStore.save(
        {
          impactAnalysis: analysis,
          reprezentantData: repData,
          art9Flag,
        },
        currentSessionId
      );
    }
    setPaywallActionType(actionType);
    setShowPaywall(true);
  };

  const saveAnalysisData = (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!currentSessionId) {
        reject(new Error("Nu există o sesiune activă"));
        return;
      }

      const updates = prepareImpactAnalysisUpdates(
        analysis,
        repData,
        entityData?.impact_analysis as ImpactAnalysisData | undefined,
        (entityData?.contact_data || null) as ContactData | null,
        entityData ?? null
      );

      if (!updates) {
        resolve();
        return;
      }

      updateMutation.mutate(
        {
          session_id: currentSessionId,
          updates,
        },
        {
          onSuccess: () => resolve(),
          onError: (errorResult) => reject(errorResult),
        }
      );
    });
  };

  const handleAnalysisComplete = async (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ) => {
    if (!currentSessionId) {
      toast({
        title: "Eroare",
        description: "Nu există o sesiune activă",
        variant: "destructive",
      });
      return;
    }

    const updates = prepareImpactAnalysisUpdates(
      analysis,
      repData,
      entityData?.impact_analysis as ImpactAnalysisData | undefined,
      (entityData?.contact_data || null) as ContactData | null,
      entityData ?? null
    );

    if (!updates) {
      toast({
        title: "Informare",
        description: "Nu există modificări de salvat",
      });
      navigate("/identificare-raportare");
      return;
    }

    if (!classifCreditCheck?.already_purchased) {
      if (!classifCreditCheck?.has_credits) {
        openPaywall("analiza_efect_perturbator", analysis, repData, true);
        return;
      }
      try {
        await deductCredits({
          action_type: CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR,
          session_id: currentSessionId,
        });
      } catch (errorResult) {
        if (errorResult instanceof InsufficientCreditsError) {
          openPaywall("analiza_efect_perturbator", analysis, repData, true);
          return;
        }
        toast({
          title: "Eroare",
          description: "Nu s-au putut deduce creditele.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      await saveAnalysisData(analysis, repData);
      toast({
        title: "Salvat cu succes",
        description: "Analiza de impact a fost salvată",
      });
      navigate("/identificare-raportare");
    } catch (errorResult) {
      console.error("Error saving impact analysis:", errorResult);
      toast({
        title: "Eroare la salvare",
        description: "Nu s-a putut salva analiza de impact",
        variant: "destructive",
      });
    }
  };

  const handleDownloadPdf = async (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ) => {
    if (!currentSessionId) {
      toast({
        title: "Eroare",
        description: "Nu există o sesiune activă",
        variant: "destructive",
      });
      return;
    }

    if (hasInsufficientCredits) {
      openPaywall("doc_analiza_efect_perturbator_pdf", analysis, repData, true);
      return;
    }

    try {
      await saveAnalysisData(analysis, repData);

      generatePdfMutation.mutate(
        { sessionId: currentSessionId, type: "impactAnalysis" },
        {
          onSuccess: async (pdfData) => {
            await generatePDFReport(
              pdfData as unknown as {
                template: unknown[];
                inputs: unknown[];
                type: "merge" | undefined;
              }
            );
          },
          onError: (errorResult) => {
            if (errorResult instanceof InsufficientCreditsError) {
              openPaywall(
                "doc_analiza_efect_perturbator_pdf",
                analysis,
                repData,
                true
              );
              return;
            }
            console.error("Error generating PDF:", errorResult);
            toast({
              title: "Eroare",
              description: "PDF-ul nu a putut fi generat. Încercați din nou.",
              variant: "destructive",
            });
          },
        }
      );
    } catch (errorResult) {
      console.error("Error saving before PDF generation:", errorResult);
      toast({
        title: "Eroare la salvare",
        description: "Nu s-a putut salva analiza de impact",
        variant: "destructive",
      });
    }
  };

  const isProcessing =
    updateMutation.isPending || generatePdfMutation.isPending;

  const handleSkipAnalysis = () => {
    toast({
      title: "Analiză omisă",
      description: "Puteți reveni oricând pentru a completa analiza",
    });
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-destructive">
                <Activity className="h-5 w-5" />
                <div>
                  <h3 className="font-semibold">
                    Eroare la încărcarea datelor
                  </h3>
                  <p className="text-sm">{error.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-lg">Se încarcă datele...</span>
          </div>
        </div>
      </div>
    );
  }

  const { isVisible } = getEfectPerturbatorStatus(entityData);
  const shouldRender = isVisible || snapshotArt9Flag;

  if (!shouldRender) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">
            Secțiune indisponibilă
          </h2>
          <p className="text-sm text-slate-500 max-w-sm">
            Analiza efect perturbator este disponibilă doar pentru entitățile
            clasificate cu impact perturbator (Art. 9).
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-2">
            Înapoi la Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const effectiveImpactAnalysis = snapshotImpactAnalysis ?? impactAnalysis;
  const effectiveReprezentantData = snapshotReprezentant
    ? {
        nume: snapshotReprezentant.nume,
        prenume: snapshotReprezentant.prenume,
        email: reprezentantData?.email ?? "",
        telefon: reprezentantData?.telefon ?? "",
        functie: snapshotReprezentant.functia,
      }
    : reprezentantData;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Activity className="h-6 md:h-8 w-6 md:w-8 text-primary" />
              <h1 className="font-heading md:text-3xl text-2xl font-bold">
                Analiza efect perturbator
              </h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/onboarding/efectul-perturbator")}
              className="gap-2"
            >
              <FileUp className="h-4 w-4" />
              Încarcă din fișier XLSX
            </Button>
          </div>
          <p className="text-sm md:text-lg text-muted-foreground">
            Evaluare conformă art. 9 din OUG 155/2024 pentru determinarea
            impactului potențial perturbator
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-2xl">
              {entityData?.nume_entitate} - {entityData?.cui}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4 md:pt-0 md:pb-6">
            <ImpactAnalysisSection
              impactAnalysis={effectiveImpactAnalysis}
              reprezentantData={effectiveReprezentantData}
              onAnalysisComplete={handleAnalysisComplete}
              onSkipAnalysis={handleSkipAnalysis}
              onDownloadPdf={handleDownloadPdf}
              isProcessing={isProcessing}
              hasInsufficientCredits={hasInsufficientCredits}
              hasInsufficientClassifCredits={hasInsufficientClassifCredits}
              isLoadingCredits={isLoadingCredits}
            />
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <h3 className="font-heading font-semibold">
                Despre această analiză
              </h3>
              <p className="text-sm text-muted-foreground">
                Această analiză este parte integrantă a procesului de evaluare
                NIS2 și este utilizată pentru a determina nivelul de impact al
                entității dumneavoastră. Rezultatele vor fi incluse automat în
                raportul de clasificare.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={paywallActionType}
      />
    </div>
  );
}
